import { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import { useManagedEntity } from 'hooks';
import { ScheduleTimeButtonGroup } from './ScheduleTimeButtonGroup';
import { CbButton } from '../Buttons/CbButton';
import { Skeleton } from '@chakra-ui/react';
export interface CbDatetimePickerProps {
  value?: Date;
  onChange(value?: Date): void;
  timezone?: string;
  disabled?: boolean;
  timeOnly?: boolean;
  allowedDate?: (date: Date) => boolean;
  thisMonthOnly?: boolean;
  testId?: string;
}
interface DumbDate {
  day?: number;
  month?: number;
  year?: number;
  hour?: number;
  minute?: number;
}
export function CbDatetimePicker({
  value,
  onChange,
  timezone,
  timeOnly,
  disabled,
  allowedDate = () => true,
  thisMonthOnly,
  testId
}: CbDatetimePickerProps) {
  const {
    entity
  } = useManagedEntity();
  timezone = timezone || entity?.timeZone;
  const date = value ? DateTime.fromJSDate(value).setZone(timezone) : defaultDate(timezone);
  const setDate = (change: DumbDate) => {
    if (disabled) return;
    onChange(date.set(change).toJSDate());
  };
  useEffect(() => {
    if (!value) {
      onChange(date.toJSDate());
    }
  }, [date, value]);

  // Select the next allowed date, if the initial date is disallowed.
  useEffect(() => {
    let newDate = date;
    if (allowedDate(newDate.setZone('local', {
      keepLocalTime: true
    }).toJSDate())) return;
    while (!allowedDate(newDate.setZone('local', {
      keepLocalTime: true
    }).toJSDate())) {
      newDate = newDate.plus({
        days: 1
      });
    }
    onChange(newDate.toJSDate());
  }, [date]);
  const conditionalProps = {} as {
    minDate: Date;
    maxDate: Date;
  };
  if (thisMonthOnly) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    conditionalProps.minDate = today;
    conditionalProps.maxDate = lastDayOfMonth;
  }
  if (!timezone) return <Skeleton height="350px" width="100%" borderRadius="2rem" />;
  return <div className="cb-datetime-picker" data-testid={testId} data-sentry-component="CbDatetimePicker" data-sentry-source-file="CbDatetimePicker.tsx">
      {!timeOnly && <>
          <DatePicker readOnly={disabled} selected={date.setZone('system', {
        keepLocalTime: true
      }).toJSDate()} onChange={(e: Date) => setDate({
        day: e.getDate(),
        month: e.getMonth() + 1,
        year: e.getFullYear()
      })} filterDate={allowedDate} inline data-testid={testId ? `${testId}-calendar` : 'date-picker'} {...conditionalProps} />
          <CbButton isDisabled={disabled} marginTop={2} width="100%" size="sm" variant="outline" onClick={() => onChange(defaultDate(timezone).toJSDate())} data-testid={testId ? `${testId}-today-button` : 'today-button'}>
            Today
          </CbButton>
        </>}
      <ScheduleTimeButtonGroup disabled={disabled} hour={date.hour} minutes={date.minute} timezone={DateTime.local().setZone(timezone).offsetNameShort ?? ''} setHour={(h: number) => setDate({
      hour: h
    })} setMinutes={(h: number) => setDate({
      minute: h
    })} data-testid={testId ? `${testId}-time-selector` : 'time-selector'} data-sentry-element="ScheduleTimeButtonGroup" data-sentry-source-file="CbDatetimePicker.tsx" />
    </div>;
}
function defaultDate(timezone) {
  const time = DateTime.now().setZone(timezone, {
    keepLocalTime: true
  });
  return time.set({
    minute: 0,
    second: 0,
    millisecond: 0
  });
}